import './styles/howToPlayDialog.css';
import Dialog from '../../subcomponents/Dialog';
import Tile from '../../subcomponents/Tile';
import { starThresholds } from '../../utils/scoreResultHelper';
import Table from '../../subcomponents/Table';

type HowToPlayDialogProps = {
  open: boolean;
  onClose: () => void;
};

function HowToPlayDialog(props: HowToPlayDialogProps) {
  const { open, onClose } = props;

  const exampleLetters = ['H', 'O', 'R', 'S', 'E'];

  const tilePointGroups: { points: number; letters: string[] }[] = [
    { points: 1, letters: ['A', 'E', 'I', 'O', 'U', 'L', 'N', 'S', 'T', 'R'] },
    { points: 2, letters: ['D', 'G'] },
    { points: 3, letters: ['B', 'C', 'M', 'P'] },
    { points: 4, letters: ['F', 'H', 'V', 'W', 'Y'] },
    { points: 5, letters: ['K'] },
    { points: 8, letters: ['J', 'X'] },
    { points: 10, letters: ['Q', 'Z'] },
  ];

  const headers = ['★', 'BLITZ', 'ENDLESS'];
  const rows = Array.from({ length: 6 }, (_, i) => [
    i + 1,
    starThresholds.blitzSimple[i],
    starThresholds.endlessSimple[i],
  ]);

  const year = new Date().getFullYear();

  return (
    <Dialog open={open} onClose={onClose} title="HOW TO PLAY">
      <div className="instructions-container">
        <span className="instructions-text">
          Tap on any tile to place the <b>NEXT</b> letter.
        </span>
        <span className="instructions-text">
          A <b>SWAP</b> is used when a letter tile is replaced, <b>UNLESS</b> replacing it completes
          a word.
        </span>
        <span className="instructions-text">
          Words can be formed in <b>ANY DIRECTION</b>, but must stretch across the <b>ENTIRE</b>{' '}
          board. Created words are <b>CLEARED</b> from the board.
        </span>
        <span className="example-text">Example:</span>
        <span className="example-tile-row">
          {exampleLetters.map((letter, idx) => (
            <Tile value={letter} key={idx} green small />
          ))}
        </span>
        <span className="example-tile-row">
          {exampleLetters.reverse().map((letter, idx) => (
            <Tile value={letter} key={idx} green small />
          ))}
        </span>
        <span className="instructions-text mt-2">
          Duplicate words are <b>NOT</b> counted. The game <b>ENDS</b> if you run out of swaps or
          time.
        </span>
        <span className="instructions-text mt-2">
          Each game mode has a <b>FIXED</b> next letter sequence per day.
        </span>
        <hr className="instructions-divider" />
        <span className="instructions-subtitle">SCORING</span>
        <span className="instructions-text">
          The <b>BONUS</b> letter is worth 2x points.
        </span>
        <span className="instructions-text">
          3 <b>BONUS</b> points for creating a word in a diagonal direction.
        </span>
        <span className="instructions-text">
          Multi-word <b>BONUS</b>: Score multiplies with each word. For example, creating 3 words at
          once is worth 3x points.
        </span>
        <div className="tile-points-container">
          {tilePointGroups.map(({ points, letters }) => (
            <div className="tile-points-row" key={points}>
              <span className="example-text">
                {points} point{points !== 1 && 's'}
              </span>
              <span className="example-tile-row">
                {letters.map((letter, idx) => (
                  <Tile value={letter} key={idx} small />
                ))}
              </span>
            </div>
          ))}
        </div>
        <hr className="instructions-divider" />
        <span className="instructions-subtitle">EARN STARS</span>
        <span className="instructions-text">
          Complete games to earn <b>STARS</b> based on performance. The required scores for each
          star amount are shown below.
        </span>
        <div className="mt-4 mb-2">
          <Table headers={headers} rows={rows} columnWidths={['', '40%', '40%']} highlightStar />
        </div>
        <span className="instructions-text mt-1">
          Earn 6 stars in a single game to receive a <b>CROWN</b> and achieve Grandmaster status.
        </span>
        <hr className="instructions-divider" />
        <div className="copyright-text mt-6">© {year} Maximilian Oberholtzer</div>
      </div>
    </Dialog>
  );
}

export default HowToPlayDialog;
